import React from "react";
import { capitalize } from "diagnostics-lib/string";
import type fr from "./fr";
import schema_errors from "./json_errors/schema_en";
import runtime_errors from "./json_errors/runtime_en";
import tooltip from "./tooltip/en";
import type { CustomTranslations } from "../../../../i18n/types";

const en: typeof fr = {
  "blocks.diagram.annotations.value.system_by_event.other": (count: number) =>
    `Other (${count} event${count > 1 ? "s" : ""})`,
  "common.link.error.invalid_url": "Invalid URL",
  "dashboard.linked_event.menu.content": ({
    linkedEventCount,
    linkedToHome,
    isPrivate,
  }: {
    linkedEventCount: number;
    linkedToHome?: React.ReactNode;
    isPrivate: boolean;
  }) => {
    const blockPrivate = isPrivate ? <>(not visible as private)</> : <></>;
    if (!linkedToHome && linkedEventCount)
      return (
        <>
          Used in {linkedEventCount} event{linkedEventCount > 1 ? "s" : ""} {blockPrivate}
        </>
      );
    else if (linkedToHome && !linkedEventCount)
      return (
        <>
          Used in the {linkedToHome} {blockPrivate}
        </>
      );
    else if (linkedToHome && linkedEventCount)
      return (
        <>
          Used in the {linkedToHome} and {linkedEventCount} event
          {linkedEventCount > 1 ? "s" : ""} {blockPrivate}
        </>
      );
    return <></>;
  },
  "dashboard.linked_event.menu.content.home_page": "home page",
  "daterangepicker.error.end_gt_limit": (end: string) =>
    `The selected end date is after the last analysis date (${end}). No data will be displayed between the selected date and this date.`,
  "daterangepicker.error.end_lt_start":
    "The end date cannot be before the start date. Please select a valid date range.",
  "daterangepicker.error.start_gt_end":
    "The start date cannot be after the end date. Please select a valid date range.",
  "daterangepicker.error.start_lt_limit": (start: string) =>
    `The selected start date is before the first analysis date (${start}). No data will be displayed between the selected date and this date.`,
  "project.dashboard.actions.admin_edit.hint": (translations: CustomTranslations) =>
    `Overwrite current modifications on the ${translations.EN.DASHBOARD.toLowerCase()} to take control and modify it`,
  "project.dashboard.actions.block.add": "Add a block",
  "project.dashboard.actions.block.open_json_editor": "Open JSON editor",
  "project.dashboard.actions.cancel": "Quit editing",
  "project.dashboard.actions.create": (translations: CustomTranslations) =>
    `Create a ${translations.EN.DASHBOARD} `,
  "project.dashboard.actions.create.error": (_translations: CustomTranslations) =>
    "An error occurred while creating the dashboard.",
  "project.dashboard.actions.create.success": (_translations: CustomTranslations) =>
    "Dashboard creation succeeded.",
  "project.dashboard.actions.delete": "Delete",
  "project.dashboard.actions.delete.error": (name: string) =>
    `An error occurred while deleting ${name}.`,
  "project.dashboard.actions.delete.success": (name: string) => `Deletion of ${name} succeeded.`,
  "project.dashboard.actions.duplicate": "Duplicate",
  "project.dashboard.actions.duplicate.error": "An error occurred while duplicating.",
  "project.dashboard.actions.duplicate.success": (name: string) =>
    `Duplication of ${name} succeeded.`,
  "project.dashboard.actions.edit": "Edit",
  "project.dashboard.actions.edit.lock.hint": (translations: CustomTranslations) =>
    `The ${translations.EN.DASHBOARD.toLowerCase()} is read-only. Only the author and users with a setup role or more on the project can edit the dashboard.`,
  "project.dashboard.actions.export": "Export",
  "project.dashboard.actions.export.error": "An error occurred while exporting.",
  "project.dashboard.actions.export.error.diagram_encode":
    "Failed to encode the diagram asset during export.",
  "project.dashboard.actions.export.error.diagram_fetch":
    "Failed to fetch the diagram asset during export.",
  "project.dashboard.actions.export.error.image_encode":
    "Failed to encode the image asset during export.",
  "project.dashboard.actions.export.error.image_fetch":
    "Failed to fetch the image asset during export.",
  "project.dashboard.actions.export.json": "JSON format",
  "project.dashboard.actions.export.partial_success": (filename: string) =>
    `Export of ${filename} succeeded, but some images could not be included in the file.`,
  "project.dashboard.actions.export.pdf": "PDF format",
  "project.dashboard.actions.export.success": (filename: string) =>
    `Export of ${filename} succeeded.`,
  "project.dashboard.actions.import": "Import",
  "project.dashboard.actions.insert_grid_item": (
    direction: "left" | "up" | "right" | "down",
  ): string => {
    switch (direction) {
      case "left":
        return "Add a block on the left";
      case "up":
        return "Add a block at the top";
      case "right":
        return "Add a block on the right";
      case "down":
        return "Add a block at the bottom";
      default:
        return "Add a block";
    }
  },
  "project.dashboard.actions.manage_sharing": "Manage sharing",
  "project.dashboard.actions.manage_sharing.disabled_hint":
    "Available only for the author and users with at least a setup role.",
  "project.dashboard.actions.move_down": "Move down",
  "project.dashboard.actions.move_grid_item": (
    direction: "left" | "up" | "right" | "down",
  ): string => {
    switch (direction) {
      case "left":
        return "Move left";
      case "up":
        return "Move up";
      case "right":
        return "Move right";
      case "down":
        return "Move down";
      default:
        return "Move";
    }
  },
  "project.dashboard.actions.move_up": "Move up",
  "project.dashboard.actions.override": `Override current modifications`,
  "project.dashboard.actions.publish": "Share",
  "project.dashboard.actions.publish.error": (name: string, translations: CustomTranslations) =>
    `An error occurred while publishing the ${translations.EN.DASHBOARD.toLowerCase()} ${name}.`,
  "project.dashboard.actions.publish.success": (name: string) =>
    `Dashboard ${name} has been shared.`,
  "project.dashboard.actions.replace": "Replace",
  "project.dashboard.actions.save": "Save",
  "project.dashboard.actions.save.error": "An error occurred while saving.",
  "project.dashboard.actions.save.success": (
    translations: CustomTranslations,
    published?: boolean,
  ) => (
    <>
      {published ? (
        <strong>The {translations.EN.DASHBOARD.toLowerCase()} has been updated</strong>
      ) : (
        <span>The {translations.EN.DASHBOARD.toLowerCase()} has been updated</span>
      )}
      <br />
      {published ? "Changes are visible by everyone" : ""}
    </>
  ),
  "project.dashboard.actions.unpublish": "Make private",
  "project.dashboard.actions.unpublish.error": (name: string, translations: CustomTranslations) =>
    `An error occurred while making the ${translations.EN.DASHBOARD.toLowerCase()} ${name} private.`,
  "project.dashboard.actions.unpublish.success": (name: string, translations: CustomTranslations) =>
    `${capitalize(translations.EN.DASHBOARD)} ${name} is now private.`,
  "project.dashboard.author.anonymous": "Anonymous",
  "project.dashboard.author.prefix": "Created by",
  "project.dashboard.block.average-losses.blank.title": "No data available for this period",
  "project.dashboard.block.comment_rich_text.editable_text.updated":
    "The content of the editable text block has been updated.",
  "project.dashboard.block.comment_rich_text.modified_by": ({
    author,
    date,
  }: {
    author: string;
    date: string;
  }) => `Edited by ${author} on ${date}`,
  "project.dashboard.block.comment_rich_text.placeholder": "Click here to add text",
  "project.dashboard.block.comment_rich_text.text.tooltip": "Edit text",
  "project.dashboard.block.comment_rich_text.title": "Editable only in Read mode",
  "project.dashboard.block.diagram.above_absolute_normalized_gap": (
    value: number,
    comparison: "gte" | "lte",
    absolute: boolean,
  ) =>
    `${absolute ? "Absolute value of normalized gap" : "Value of normalized gap"} ${comparison === "gte" ? "greater than" : "lower than"} ${value} σ`,
  "project.dashboard.block.diagram.above_absolute_threshold": (
    value: number,
    comparison: "gte" | "lte",
    unit: string,
  ) => `Absolute value ${comparison === "gte" ? "greater than" : "lower than"} ${value} ${unit}`,
  "project.dashboard.block.diagram.above_threshold": (value: number, unit: string) =>
    `Above the threshold of ${value} ${unit}`,
  "project.dashboard.block.diagram.below_threshold": (value: number, unit: string) =>
    `Below the threshold of ${value} ${unit}`,
  "project.dashboard.block.diagram.blank.subtitle":
    "Use the right sidebar to add an image and annotations",
  "project.dashboard.block.diagram.blank.title": "No diagram available to display",
  "project.dashboard.block.diagram.display.all":
    "Display detailed annotations by default in read mode",
  "project.dashboard.block.diagram.display.dark_mode": "Dark mode for quantities",
  "project.dashboard.block.diagram.display.last_analysis":
    "Automatically update if a new analysis is available",
  "project.dashboard.block.diagram.display.last_analysis.detail":
    "If a new, more recent point is loaded, the data displayed will be automatically refreshed to this date",
  "project.dashboard.block.diagram.display.roles": "Display by default quantities role",
  "project.dashboard.block.diagram.display.title": "Display",
  "project.dashboard.block.diagram.displayed_analysis": `Displayed analysis`,
  "project.dashboard.block.diagram.displayed_analysis.details": `Automatic update in case of new analysis is activated. If a new, more recent point is loaded, the data displayed will be automatically refreshed to this date`,
  "project.dashboard.block.diagram.displayed_period": "Period",
  "project.dashboard.block.diagram.evolution.detail":
    "Evolution is calculated by comparing the value with the previous point:",
  "project.dashboard.block.diagram.evolution.detail.computation":
    "This trend is calculated on the basis of absolute values for gaps, normalized gaps, external gaps and impacts on performance indicators",
  "project.dashboard.block.diagram.evolution.detail.down":
    "A downward arrow appears if the change is less than -20%",
  "project.dashboard.block.diagram.evolution.detail.up":
    "An upward arrow appears if the change is greater than 20%",
  "project.dashboard.block.diagram.evolution.down":
    "The value has decreased by more than 20% compared to the previous point",
  "project.dashboard.block.diagram.evolution.down.absolute":
    "The value has decreased by more than 20% in absolute value compared to the previous point",
  "project.dashboard.block.diagram.evolution.up":
    "The value has increased by more than 20% compared to the previous point",
  "project.dashboard.block.diagram.evolution.up.absolute":
    "The value has increased by more than 20% in absolute value compared to the previous point",
  "project.dashboard.block.diagram.expected_unavailable": "Expected value not available",
  "project.dashboard.block.diagram.external_impacts_unavailable": (name: string) =>
    `Impact on ${name} not available`,
  "project.dashboard.block.diagram.false_detection": "Put in false detection",
  "project.dashboard.block.diagram.fault_not_detected": "Fault not detected",
  "project.dashboard.block.diagram.full_annotation.close": "Minimize the annotation",
  "project.dashboard.block.diagram.full_annotation.open": "Expand the annotation",
  "project.dashboard.block.diagram.invalid_quantity": "Invalid value",
  "project.dashboard.block.diagram.menu": "Diagram visualization settings",
  "project.dashboard.block.diagram.menu.details": "Display detailed annotations",
  "project.dashboard.block.diagram.menu.display_invalid": "Display invalid point",
  "project.dashboard.block.diagram.menu.display_invalid.description":
    "If enabled, invalid measure is displayed",
  "project.dashboard.block.diagram.menu.display_roles": "Display the roles",
  "project.dashboard.block.diagram.menu.faults": "Displayed fault statuses",
  "project.dashboard.block.diagram.menu.quantities": "Quantities",
  "project.dashboard.block.diagram.no_data": "No content to display",
  "project.dashboard.block.diagram.role_label": "Roles",
  "project.dashboard.block.diagram.std_unavailable": "Standard deviation not available",
  "project.dashboard.block.diagram.threshold.error.absolute": "Values must be positive",
  "project.dashboard.block.diagram.threshold.error.inconsistent":
    "Selected thresholds are inconsistent. It is not possible to define a “Greater than or equal to” threshold smaller than a “Less than or equal to” threshold.",
  "project.dashboard.block.diagram.undefined_gap": "Gap cannot be calculated",
  "project.dashboard.block.diagram.undefined_normalized_gap": "Normalized gap cannot be calculated",
  "project.dashboard.block.diagram.undefined_unexplained_losses": (unexplainedLosses: string) =>
    `${unexplainedLosses} cannot be calculated`,
  "project.dashboard.block.image.blank.subtitle": "Use the section on the right to import an image",
  "project.dashboard.block.image.blank.title": "No image available to display",
  "project.dashboard.block.kind": (kind: string): string =>
    kind === "TEXT"
      ? "Text"
      : kind === "HEADING"
        ? "Heading"
        : kind === "TABLE"
          ? "Table"
          : kind === "PLOT"
            ? "Chart"
            : kind === "IMAGE"
              ? "Image"
              : kind === "DIAGRAM"
                ? "Diagram"
                : "",
  "project.dashboard.block.loading.text":
    "The content is being loaded, this may take a few moments.",
  "project.dashboard.block.loading.title": "Loading in progress",
  "project.dashboard.block.modal.addCustom": "Custom",
  "project.dashboard.block.modal.addPreset": "Preset",
  "project.dashboard.block.period": (from: string, to: string, duration: string) =>
    `Data loaded from ${from} to ${to} (${duration})`,
  "project.dashboard.block.plot.axis_hint": "Double-click to display all data",
  "project.dashboard.block.plot.empty_series": (names: string[]) =>
    names.length > 1 ? (
      <>
        Series <strong>{names.join(", ")}</strong> contain no data to be presented
      </>
    ) : (
      <>
        Series <strong>{names}</strong> contains no data to be presented
      </>
    ),
  "project.dashboard.block.plot.error.config.description":
    "Please check and modify the configuration to allow the creation of the graph.",
  "project.dashboard.block.plot.error.config.title":
    "The configuration of the series and graphs is incorrect.",
  "project.dashboard.block.plot.error.data.description":
    "Please add data using the sidebar to the right.",
  "project.dashboard.block.plot.error.data.title": "No data selected",
  "project.dashboard.block.plot.na_category": "not specified",
  "project.dashboard.block.production-monitoring.blank.title": "No data available to display",
  "project.dashboard.block.rich_text.inline_editor.equation.title":
    "Double-click to edit the equation.",
  "project.dashboard.block.rich_text.modal.equation.input_label": "Equation (in LaTeX)",
  "project.dashboard.block.rich_text.modal.equation.preview_label": "Preview",
  "project.dashboard.block.rich_text.modal.equation.preview_label.empty": "No equation to preview",
  "project.dashboard.block.rich_text.modal.equation.subtitle": "Insert a LaTeX equation",
  "project.dashboard.block.rich_text.modal.equation.tooltip.text.description":
    "Examples of LaTeX syntax are available in the user guide.",
  "project.dashboard.block.rich_text.modal.equation.tooltip.text.link":
    "Link to the dedicated section",
  "project.dashboard.block.rich_text.modal.insert": "Insert",
  "project.dashboard.block.rich_text.modal.link.error": "Invalid URL",
  "project.dashboard.block.rich_text.modal.link.label": "Link",
  "project.dashboard.block.rich_text.modal.link.placeholder": "Enter URL (starting with https://)",
  "project.dashboard.block.rich_text.toolbar.bold": "Bold",
  "project.dashboard.block.rich_text.toolbar.equation.insert": "Insert an equation",
  "project.dashboard.block.rich_text.toolbar.italic": "Italic",
  "project.dashboard.block.rich_text.toolbar.link.error.invalid_url": "Invalid URL",
  "project.dashboard.block.rich_text.toolbar.link.insert": "Insert link",
  "project.dashboard.block.rich_text.toolbar.link.remove": "Remove link",
  "project.dashboard.block.rich_text.toolbar.link.replace": "Replace link",
  "project.dashboard.block.sub_subtitle.placeholder": "Sub-subheading (3000 characters maximum)",
  "project.dashboard.block.subtitle.placeholder": "Subheading (3000 characters maximum)",
  "project.dashboard.block.template.anomaly_detection_last_week_alerts.title": "Last week alerts",
  "project.dashboard.block.template.average-losses-preset.title": "Average losses",
  "project.dashboard.block.template.category.advanced": "Advanced",
  "project.dashboard.block.template.category.plot": "Chart",
  "project.dashboard.block.template.category.preset": "Preset",
  "project.dashboard.block.template.category.text": "Text",
  "project.dashboard.block.template.category.visual": "Visual",
  "project.dashboard.block.template.comment.title": "Editable text",
  "project.dashboard.block.template.costImpact.title": "Estimated financial impact",
  "project.dashboard.block.template.diagram.sub_subtitle": "Diagram",
  "project.dashboard.block.template.empty.description": "Advanced and more flexible block",
  "project.dashboard.block.template.empty.title": "JSON",
  "project.dashboard.block.template.heading.sub_subtitle": "Sub-subheading",
  "project.dashboard.block.template.heading.subtitle": "Subheading",
  "project.dashboard.block.template.heading.title": "Heading",
  "project.dashboard.block.template.image.sub_subtitle": "Image",
  "project.dashboard.block.template.image.subtitle": "Image",
  "project.dashboard.block.template.image.title": "Image",
  "project.dashboard.block.template.priority-fault-events-preset.title": "Priority fault events",
  "project.dashboard.block.template.production-monitoring-preset.title": "Production monitoring",
  "project.dashboard.block.template.scatterplot.description":
    "Chart of an element according to another (XY graph)",
  "project.dashboard.block.template.scatterplot.title": "Scatterplot",
  "project.dashboard.block.template.table.description": "Display of tabular data",
  "project.dashboard.block.template.table.title": "Table",
  "project.dashboard.block.template.text.title": "Text",
  "project.dashboard.block.template.timeseries.description": "Analysis over time",
  "project.dashboard.block.template.timeseries.title": "Time series",
  "project.dashboard.block.text.placeholder": "Add a text (3000 characters maximum)",
  "project.dashboard.block.title.placeholder": "Heading (3000 characters maximum)",
  "project.dashboard.block.zoom.reset.title": "Reset the zoom",
  "project.dashboard.delete.message.linked_events": ({
    linkedEventCount,
    isLinkedToHome,
    translations,
  }: {
    linkedEventCount: number;
    isLinkedToHome: boolean;
    translations: CustomTranslations;
  }) => {
    let sentence = <></>;
    if (!isLinkedToHome && linkedEventCount)
      sentence = (
        <>
          <strong>the {linkedEventCount === 1 ? "event" : ` ${linkedEventCount} events`}</strong>{" "}
          that use it.
        </>
      );
    else if (isLinkedToHome && !linkedEventCount) sentence = <strong>the home page.</strong>;
    else if (isLinkedToHome && linkedEventCount)
      sentence = (
        <>
          <strong>
            the home page and{" "}
            {linkedEventCount === 1 ? "the event" : `the ${linkedEventCount} events`}{" "}
          </strong>{" "}
          that use it.
        </>
      );

    return (
      <>
        Following this action, the {translations.EN.DASHBOARD.toLowerCase()} will no longer be
        visible in {sentence}
      </>
    );
  },
  "project.dashboard.description.placeholder": (translations: CustomTranslations) =>
    `Add a ${translations.EN.DASHBOARD.toLowerCase()} description (3000 characters maximum)`,
  "project.dashboard.editor.blank_state.subtitle": "Select a block to access its settings",
  "project.dashboard.editor.blank_state.title": "No block is selected",
  "project.dashboard.editor.copy.error": "An error occurred while copying the JSON content.",
  "project.dashboard.editor.copy.success":
    "The JSON content of the block has been copied to the clipboard.",
  "project.dashboard.editor.gui.position": "Block position:",
  "project.dashboard.editor.gui.section.aggregation_range.title": "Aggregation period",
  "project.dashboard.editor.gui.section.annotation_settings.absolute_threshold": "Absolute value",
  "project.dashboard.editor.gui.section.annotation_settings.action": "Add",
  "project.dashboard.editor.gui.section.annotation_settings.area": "Rectangle with label",
  "project.dashboard.editor.gui.section.annotation_settings.assigned_threshold": "Assigned to",
  "project.dashboard.editor.gui.section.annotation_settings.disabled_absolute_threshold":
    "Option only available for gaps",
  "project.dashboard.editor.gui.section.annotation_settings.disabled_amplitude":
    "Only available for faults detected by Metroscope",
  "project.dashboard.editor.gui.section.annotation_settings.disabled_expected_gap":
    "Available only for quantities used as model output",
  "project.dashboard.editor.gui.section.annotation_settings.disabled_external_impacts":
    "Available only for quantities used as external gap",
  "project.dashboard.editor.gui.section.annotation_settings.disabled_normalized_gap":
    "Available only for quantities used as observables",
  "project.dashboard.editor.gui.section.annotation_settings.disabled_std":
    "Available only for measured quantities",
  "project.dashboard.editor.gui.section.annotation_settings.dot": "Point with label",
  "project.dashboard.editor.gui.section.annotation_settings.label_only": "Label only",
  "project.dashboard.editor.gui.section.annotation_settings.system_filter":
    "Fault included in the calculation",
  "project.dashboard.editor.gui.section.annotation_settings.system_value":
    "Display the impact on performance indicators:",
  "project.dashboard.editor.gui.section.annotation_settings.system_value.distribution_by_event.description":
    (performanceIndicator: string) => (
      <>
        The distribution is displayed only for the power produced{" "}
        <strong>{performanceIndicator}</strong>.
      </>
    ),
  "project.dashboard.editor.gui.section.annotation_settings.system_value.distribution_by_event.toggle":
    "Distribution by event",
  "project.dashboard.editor.gui.section.annotation_settings.system_value.distribution_by_event.tooltip.disabled":
    "Only available for produced power",
  "project.dashboard.editor.gui.section.annotation_settings.threshold_placeholder": "Value",
  "project.dashboard.editor.gui.section.annotation_settings.title": "Annotation settings:",
  "project.dashboard.editor.gui.section.annotation_settings.value": "Displayed values:",
  "project.dashboard.editor.gui.section.data.add_button": "Add",
  "project.dashboard.editor.gui.section.data.title": "Data",
  "project.dashboard.editor.gui.section.display.cycle.title": "Display intercycles",
  "project.dashboard.editor.gui.section.display.invalidated_data_points":
    "Display invalidated data points",
  "project.dashboard.editor.gui.section.display.marker_info.empty": "Select marker type",
  "project.dashboard.editor.gui.section.display.marker_info.title": "Marker information",
  "project.dashboard.editor.gui.section.display.publication.title": "Display publication markers",
  "project.dashboard.editor.gui.section.display.sync.title": "Synchronize zoom",
  "project.dashboard.editor.gui.section.display.temporal_zoom.title":
    "Default time range displayed",
  "project.dashboard.editor.gui.section.display.title": "Display settings",
  "project.dashboard.editor.gui.section.displayed_value.analysis": "Analysis",
  "project.dashboard.editor.gui.section.displayed_value.description": () => (
    <>
      <strong>Analysis</strong>: The value displayed is that of the selected analysis.
      <br />
      <strong>Period</strong>: The value displayed will be the weighted average of the values
      observed over the aggregation period (based on the time between 2 points) when the value is a
      power. Otherwise, the values displayed will be the median, minimum and maximum values observed
      over the aggregation period.
    </>
  ),
  "project.dashboard.editor.gui.section.displayed_value.period": "Period",
  "project.dashboard.editor.gui.section.displayed_value.title": "Displayed value",
  "project.dashboard.editor.gui.section.encoding.title": (n?: number) =>
    typeof n === "number" ? `Axis ${n + 1}` : "Axis",
  "project.dashboard.editor.gui.section.encoding.zoom.max.label": "Max",
  "project.dashboard.editor.gui.section.encoding.zoom.max.placeholder": "max",
  "project.dashboard.editor.gui.section.encoding.zoom.min.label": "Min",
  "project.dashboard.editor.gui.section.encoding.zoom.min.placeholder": "min",
  "project.dashboard.editor.gui.section.encoding.zoom.title": "Axis range",
  "project.dashboard.editor.gui.section.evolution.title": "Evolution",
  "project.dashboard.editor.gui.section.group_by_unit.title": "Group by unit",
  "project.dashboard.editor.gui.section.period.title": "Time range",
  "project.dashboard.editor.gui.section.rule.max.label": "Max",
  "project.dashboard.editor.gui.section.rule.min.label": "Min",
  "project.dashboard.editor.gui.section.rule.plot_bands": "Display overtaking threshold zones",
  "project.dashboard.editor.gui.section.rule.title": "Threshold",
  "project.dashboard.editor.gui.section.time_range.title": "Loaded time range",
  "project.dashboard.editor.gui.section.title.annotation": (count: number) =>
    `Annotation${count > 1 ? "s" : ""} (${count})`,
  "project.dashboard.editor.gui.section.title.content": "Content",
  "project.dashboard.editor.gui.section.title.diagram": "Diagram title",
  "project.dashboard.editor.gui.section.title.image": "Title",
  "project.dashboard.editor.gui.section.title.label": "Label",
  "project.dashboard.editor.gui.section.title.plot": "Chart title",
  "project.dashboard.editor.gui.section.title.title_image": "Image title",
  "project.dashboard.editor.gui.title": (kind: string) => `${kind} settings`,
  "project.dashboard.editor.image.file.action": "Import",
  "project.dashboard.editor.image.file.label": "Image",
  "project.dashboard.editor.image.title.label": "Image title",
  "project.dashboard.editor.json.title": (kind: string) => `Settings of ${kind} (JSON)`,
  "project.dashboard.editor.no_editor.subtitle": "Only block content can be edited.",
  "project.dashboard.editor.no_editor.title": "No settings to edit",
  "project.dashboard.encoding.kind": (kind: string, manual?: boolean): string =>
    kind === "QUANTITY"
      ? "Quantity"
      : kind === "FAULT"
        ? manual
          ? "Non-modelled fault"
          : "Fault"
        : kind === "SYSTEM"
          ? "System"
          : "",
  "project.dashboard.encoding.unknown": "Unknown",
  "project.dashboard.fault.non_modelled": "Non-modelled",
  "project.dashboard.fault.not_detected": "Not detected",
  "project.dashboard.fault.status.confirmed": "Confirmed",
  "project.dashboard.fault.status.false_detection": "False detection",
  "project.dashboard.fault.status.suspected": "Suspected",
  "project.dashboard.fault.status.to_analyse": "To be analysed",
  "project.dashboard.found.error": (translations: CustomTranslations) =>
    `${capitalize(translations.EN.DASHBOARD)} not found.`,
  "project.dashboard.header.modal.footer.duplicate_button": "Duplicate",
  "project.dashboard.header.modal.footer.edit_button": "Edit",
  "project.dashboard.header.modal.form.changed_dashboard": (translations: CustomTranslations) =>
    `Edit ${translations.EN.DASHBOARD.toLowerCase()}`,
  "project.dashboard.header.modal.form.changed_dashboard.content": ({
    dashboardName,
    linkedEventCount,
    isLinkedToHome,
  }: {
    dashboardName: string;
    linkedEventCount: number;
    isLinkedToHome: boolean;
  }) => {
    let sentence = <></>;
    if (!isLinkedToHome && linkedEventCount)
      sentence = (
        <>
          The changes will be visible in{" "}
          <strong>{linkedEventCount > 1 ? `the ${linkedEventCount} events` : "the event"}</strong>{" "}
          that use it.
        </>
      );
    else if (isLinkedToHome && !linkedEventCount)
      sentence = <>The changes will be visible in the home page.</>;
    else if (isLinkedToHome && linkedEventCount)
      sentence = (
        <>
          The changes will be visible in the home page and{" "}
          <strong>{linkedEventCount > 1 ? `the ${linkedEventCount} events` : "the event"}</strong>{" "}
          that use it.
        </>
      );

    return (
      <>
        Are you sure you want to modify the dashboard <strong>{dashboardName}</strong>? {sentence}{" "}
        <br />
        <br />
        You can also duplicate this dashboard to preserve the original before proceeding with your
        changes.
      </>
    );
  },
  "project.dashboard.lite.header.button": (translations: CustomTranslations) =>
    `Go to ${translations.EN.DASHBOARD.toLowerCase()}`,
  "project.dashboard.period.prefix": "Time range",
  "project.dashboard.placeholder.or": "or",
  "project.dashboard.placeholder.text": (translations: CustomTranslations) =>
    `import a ${translations.EN.DASHBOARD.toLowerCase()} from a JSON file`,
  "project.dashboard.plot.tooltip.faultStatus": "Status:",
  "project.dashboard.plot.tooltip.validity": "Validity:",
  "project.dashboard.print.date.title": "Export date:",
  "project.dashboard.print.export_by.title": "By:",
  "project.dashboard.resource.column": (column: string) => {
    if (column === "measured") return "Measured value";
    if (column === "simulated") return "Simulated value";
    if (column === "std") return "Standard deviation";
    if (column === "validity") return "Measure validity";
    if (column === "offsetExpected") return "Offset";
    if (column === "expected") return "Expected value";
    if (column === "gap") return "Gap";
    if (column === "faultStatus") return "Fault status";
    if (column === "normalizedGap") return "Normalized gap";
    if (column === "reparability") return "Outage time for repair";
    if (column === "status") return "Status";
    if (column === "real") return "Amplitude";
    if (column === "confidence") return "Confidence level";
    if (column === "globalImpact") return "Global impact";
    if (column === "quantityImpacts" || column === "systemImpact" || column === "noSystemImpacts")
      return `Impact on`;
    if (column === "externalImpacts") return `External impact on`;
    if (column === "group") return `Cycle`;
    if (column === "date") return `Date`;
    if (column === "hour") return `Hour of day`;
    if (column === "dayOfYear") return `Day of year`;
    return capitalize(column);
  },
  "project.dashboard.resource.column.annotation": (column: string) => {
    if (column === "measured") return "Measured";
    if (column === "simulated") return "Simulated";
    if (column === "std") return "Standard deviation";
    if (column === "validity") return "Measure validity";
    if (column === "offsetExpected") return "Offset";
    if (column === "expected") return "Expected";
    if (column === "gap") return "Gap";
    if (column === "faultStatus") return "Fault status";
    if (column === "normalizedGap") return "Normalized gap";
    if (column === "reparability") return "Outage time for repair";
    if (column === "status") return "Status";
    if (column === "real") return "Amplitude";
    if (column === "confidence") return "Confidence level";
    if (column === "globalImpact") return "Global impact";
    if (column === "quantityImpacts" || column === "systemImpact" || column === "noSystemImpacts")
      return ``;
    if (column === "externalImpacts") return `External impact on`;
    if (column === "group") return `Cycle`;
    if (column === "date") return `Date`;
    if (column === "hour") return `Hour of day`;
    if (column === "dayOfYear") return `Day of year`;
    return capitalize(column);
  },
  "project.dashboard.resource.column.annotation.label.systemImpact": (
    translations: CustomTranslations,
    quantity: string,
    hasMultiplePIs: boolean,
  ) =>
    hasMultiplePIs
      ? `${capitalize(translations.EN.LOSSES)} (${quantity}): `
      : `${capitalize(translations.EN.LOSSES)}: `,
  "project.dashboard.resource.open_quantities": "Access the list of quantities",
  "project.dashboard.role.label": (
    role:
      | "MODEL_INPUT"
      | "OBSERVABLE"
      | "INACTIVE_OBSERVABLE"
      | "PERFORMANCE_INDICATOR"
      | "FAULT"
      | "SYSTEM"
      | "QUANTITY"
      | "UNEXPLAINED_LOSSES"
      | "OTHER_QUANTITY",
    translations: CustomTranslations,
  ): string => {
    switch (role) {
      case "MODEL_INPUT":
        return "Model input";
      case "OBSERVABLE":
        return "Observable";
      case "INACTIVE_OBSERVABLE":
        return "Deactivated observable";
      case "PERFORMANCE_INDICATOR":
        return "Performance indicator";
      case "FAULT":
        return "Fault";
      case "SYSTEM":
        return "System";
      case "QUANTITY":
        return "Quantity";
      case "UNEXPLAINED_LOSSES":
        return `${capitalize(translations.EN.UNEXPLAINED)} ${translations.EN.LOSSES.toLowerCase()}`;
      case "OTHER_QUANTITY":
        return "Other quantity";
      default:
        throw new Error(role satisfies never);
    }
  },
  "project.dashboard.state.editing": (name: string | null) =>
    name ? `Locked from editing while being edited by ${name}` : "Being Edited",
  "project.dashboard.state.unsaved": "Unsaved changes",
  "project.dashboard.status.published": "Shared",
  "project.dashboard.status.readonly": "Read only",
  "project.dashboard.title.empty": "Untitled",
  "project.dashboard.title.placeholder": (translations: CustomTranslations) =>
    `Add a ${translations.EN.DASHBOARD.toLowerCase()} title`,
  "project.dashboard.transaction.admin_take_over.action": "Quit",
  "project.dashboard.transaction.admin_take_over.text": (translations: CustomTranslations) =>
    `An administrator has taken control of this ${translations.EN.DASHBOARD.toLowerCase()}. All unsaved changes have been lost. You will be redirected to the ${translations.EN.DASHBOARD.toLowerCase()} in read-only mode.`,
  "project.dashboard.transaction.admin_take_over.title": (translations: CustomTranslations) =>
    `${capitalize(translations.EN.DASHBOARD)} editing temporarily blocked`,
  "project.dashboard.transaction.delete.submit_action": "Delete",
  "project.dashboard.transaction.delete.subtitle": (translations: CustomTranslations) =>
    `Do you want to delete the ${translations.EN.DASHBOARD.toLowerCase()}:`,
  "project.dashboard.transaction.delete.title": (translations: CustomTranslations) =>
    `Delete the ${translations.EN.DASHBOARD.toLowerCase()}`,
  "project.dashboard.transaction.discard.cancel_action": "Keep editing",
  "project.dashboard.transaction.discard.submit_action": "Discard",
  "project.dashboard.transaction.discard.subtitle": (translations: CustomTranslations) =>
    `Do you want to discard the changes made to the ${translations.EN.DASHBOARD.toLowerCase()}:`,
  "project.dashboard.transaction.discard.title": "Discard changes",
  "project.dashboard.transaction.duplicate.error.conflict": "The name already exists",
  "project.dashboard.transaction.duplicate.error.empty": "The name cannot be empty",
  "project.dashboard.transaction.duplicate.name.label": "Name",
  "project.dashboard.transaction.duplicate.submit_action": "Duplicate",
  "project.dashboard.transaction.duplicate.title": (translations: CustomTranslations) =>
    `Duplicate the ${translations.EN.DASHBOARD.toLowerCase()}`,
  "project.dashboard.transaction.import_image.error.file_too_large":
    "The file size exceeds the allowed limit of 20 Mo.",
  "project.dashboard.transaction.import_image.error.invalid_file_type":
    "The file type is not supported. Please upload a .jpeg, .png, or .svg file.",
  "project.dashboard.transaction.import_image.error.no_file":
    "An error occurred while uploading the image. Please try again.",
  "project.dashboard.transaction.import_image.error.to_many_files":
    "You can only upload one image at a time.",
  "project.dashboard.transaction.import_image.error.upload_error":
    "An error occurred while uploading the image. Please try again.",
  "project.dashboard.transaction.import_image.title": "Import image",
  "project.dashboard.transaction.manage_sharing.access.check.readonly": "Read only",
  "project.dashboard.transaction.manage_sharing.access.check.readwrite": "Read and write",
  "project.dashboard.transaction.manage_sharing.access.hint.readonly": (
    translations: CustomTranslations,
  ) =>
    `Only the author and users with at least a setup role for the project can edit the ${translations.EN.DASHBOARD.toLowerCase()}.`,
  "project.dashboard.transaction.manage_sharing.access.hint.readwrite": (
    translations: CustomTranslations,
  ) =>
    `All users with at least a write role for the project can edit the ${translations.EN.DASHBOARD.toLowerCase()}.`,
  "project.dashboard.transaction.manage_sharing.access.title": "Locking",
  "project.dashboard.transaction.manage_sharing.error": (translations: CustomTranslations) =>
    `An error occurred when sharing the ${translations.EN.DASHBOARD.toLowerCase()}.`,
  "project.dashboard.transaction.manage_sharing.private_to_share.message.linked_events": ({
    linkedEventCount,
    isLinkedToHome,
    translations,
  }: {
    linkedEventCount: number;
    isLinkedToHome: boolean;
    translations: CustomTranslations;
  }) => {
    let sentence = <></>;
    if (linkedEventCount && !isLinkedToHome)
      sentence = (
        <>
          <strong>
            {linkedEventCount === 1 ? <>the event</> : <>the {linkedEventCount} events</>}
          </strong>{" "}
          that use it.
        </>
      );
    else if (!linkedEventCount && isLinkedToHome) sentence = <strong>the home page.</strong>;
    else if (linkedEventCount && isLinkedToHome)
      sentence = (
        <>
          <strong>
            the home page and{" "}
            {linkedEventCount === 1 ? <>the event</> : <>the {linkedEventCount} events</>}
          </strong>{" "}
          that use it.
        </>
      );

    return (
      <>
        Following this action, the {translations.EN.DASHBOARD.toLowerCase()} will be visible in{" "}
        {sentence}
      </>
    );
  },
  "project.dashboard.transaction.manage_sharing.share_to_private.message.linked_events": ({
    linkedEventCount,
    isLinkedToHome,
    translations,
  }: {
    linkedEventCount: number;
    isLinkedToHome: boolean;
    translations: CustomTranslations;
  }) => {
    let sentence = <></>;
    if (linkedEventCount && !isLinkedToHome)
      sentence = (
        <>
          <strong>
            {linkedEventCount === 1 ? <>the event</> : <>the {linkedEventCount} events</>}
          </strong>{" "}
          that use it.
        </>
      );
    else if (!linkedEventCount && isLinkedToHome) sentence = <strong>the home page.</strong>;
    else if (linkedEventCount && isLinkedToHome)
      sentence = (
        <>
          <strong>
            the home page and{" "}
            {linkedEventCount === 1 ? <>the event</> : <>the {linkedEventCount} events</>}
          </strong>{" "}
          that use it.
        </>
      );

    return (
      <>
        Following this action, the {translations.EN.DASHBOARD.toLowerCase()} will no longer be
        visible in {sentence}
      </>
    );
  },
  "project.dashboard.transaction.manage_sharing.success": (translations: CustomTranslations) =>
    `The ${translations.EN.DASHBOARD.toLowerCase()} sharing has been updated.`,
  "project.dashboard.transaction.manage_sharing.validate": "Save",
  "project.dashboard.transaction.manage_sharing.visibility.check.private": "Private",
  "project.dashboard.transaction.manage_sharing.visibility.check.shared": "Shared",
  "project.dashboard.transaction.manage_sharing.visibility.hint.private": (
    translations: CustomTranslations,
  ) =>
    `You are the only person who can view and edit the ${translations.EN.DASHBOARD.toLowerCase()}.`,
  "project.dashboard.transaction.manage_sharing.visibility.hint.shared": (
    translations: CustomTranslations,
  ) =>
    `The ${translations.EN.DASHBOARD.toLowerCase()} is visible to all people with access to the project.`,
  "project.dashboard.transaction.manage_sharing.visibility.title": "Visibility",
  "project.dashboard.transaction.publish.submit_action": "Share",
  "project.dashboard.transaction.publish.text_1": (translations: CustomTranslations) =>
    `The ${translations.EN.DASHBOARD.toLowerCase()}:`,
  "project.dashboard.transaction.publish.text_2":
    "will be accessible to all people with access to this project.",
  "project.dashboard.transaction.publish.title": (translations: CustomTranslations) =>
    `Share the ${translations.EN.DASHBOARD.toLowerCase()}:`,
  "project.dashboard.transaction.save.cancel_action": "Discard changes",
  "project.dashboard.transaction.save.submit_action": "Save and quit",
  "project.dashboard.transaction.save.subtitle": (translations: CustomTranslations) =>
    `Do you want to save your changes to the ${translations.EN.DASHBOARD.toLowerCase()} before quitting?`,
  "project.dashboard.transaction.save.title": "You have unsaved changes",
  "project.dashboard.transaction.unlock.action": "Continue",
  "project.dashboard.transaction.unlock.hint": (translations: CustomTranslations) =>
    `The ${translations.EN.DASHBOARD.toLowerCase()} is read-only. Only the author and users with a setup role or more on the project can edit the ${translations.EN.DASHBOARD.toLowerCase()}.`,
  "project.dashboard.transaction.unlock.text": (translations: CustomTranslations) =>
    `Would you like to take control of this ${translations.EN.DASHBOARD.toLowerCase()} and overwrite the changes?`,
  "project.dashboard.transaction.unlock.title": "Override modifications",
  "project.dashboard.transaction.unlock.warning":
    "Please note that current modifications will be permanently lost.",
  "project.dashboard.transaction.unpublish.submit_action": "Make private",
  "project.dashboard.transaction.unpublish.text_2":
    "will only be visible and editable by its creator.",
  "project.dashboard.transaction.unpublish.title": (translations: CustomTranslations) =>
    `Make the ${translations.EN.DASHBOARD.toLowerCase()} private`,
  "project.dashboards.empty.subtitle": (translations: CustomTranslations) =>
    `Custom ${translations.EN.DASHBOARD.toLowerCase()} allow for a better understanding and utilisation of data.`,
  "project.dashboards.empty.title": (translations: CustomTranslations) =>
    `No ${translations.EN.DASHBOARD.toLowerCase()} is defined`,
  "project.dashboards.import.action.validation": "Import",
  "project.dashboards.import.button.text": (translations: CustomTranslations) =>
    `Import a ${translations.EN.DASHBOARD.toLowerCase()}`,
  "project.dashboards.import.errors.drop": "An error occurred while dropping the file",
  "project.dashboards.import.fail": (translations: CustomTranslations) =>
    `An error occurred while importing the ${translations.EN.DASHBOARD.toLowerCase()}.`,
  "project.dashboards.import.identifier_mapping_title": "Resources to replace",
  "project.dashboards.import.missing_identifier":
    "The file contains unidentified resources. Please enter the desired values for these resources.",
  "project.dashboards.import.modal.subtitle": (translations: CustomTranslations) =>
    `Your ${translations.EN.DASHBOARD.toLowerCase()} will be created based on the imported file.`,
  "project.dashboards.import.modal.title": (translations: CustomTranslations) =>
    `Import a ${translations.EN.DASHBOARD.toLowerCase()}`,
  "project.dashboards.import.options.publish": (translations: CustomTranslations) =>
    `Share ${translations.EN.DASHBOARD.toLowerCase()}`,
  "project.dashboards.import.options.replace": (translations: CustomTranslations) =>
    `Replace current ${translations.EN.DASHBOARD.toLowerCase()}?`,
  "project.dashboards.import.select_identifier": "Select",
  "project.dashboards.import.success": (translations: CustomTranslations) =>
    `${capitalize(translations.EN.DASHBOARD)} import successful.`,
  "project.dashboards.import.unknown_identifier_kind": (identifiers: string[]) =>
    "File contains unidentifiable resources (" +
    identifiers.join(", ") +
    "). Some charts may need additional setup afterward.",
  "project.dashboards.import.warning.summary": "The dashboard was imported without them.",
  "project.dashboards.import.warning.title": "Some images were not imported",
  "project.dashboards.listing.published.empty": (translations: CustomTranslations) =>
    `No shared ${translations.EN.DASHBOARD.toLowerCase()}`,
  "project.dashboards.listing.published.title": "Shared",
  "project.dashboards.listing.unpublished.empty": (translations: CustomTranslations) =>
    `No private ${translations.EN.DASHBOARD.toLowerCase()}`,
  "project.dashboards.listing.unpublished.title": "Private",
  "project.dashboards.navigation": (translations: CustomTranslations): string =>
    `${capitalize(translations.EN.DASHBOARDS)}`,
  "project.dashboards.no_analyses.subtitle": (translations: CustomTranslations) =>
    `Please create at least one analysis to access and use the ${translations.EN.DASHBOARDS.toLowerCase()}`,
  "project.dashboards.no_analyses.title": (translations: CustomTranslations) =>
    `${capitalize(translations.EN.DASHBOARDS)} unavailable without an analysis in the project`,
  "project.dashboards.table.column.actions": "Actions",
  "project.dashboards.table.column.author": "Created by",
  "project.dashboards.table.column.createdAt": "Creation",
  "project.dashboards.table.column.description": "Description",
  "project.dashboards.table.column.editor": "Modified by",
  "project.dashboards.table.column.published": "Status",
  "project.dashboards.table.column.title": "Name",
  "project.dashboards.table.column.updatedAt": "Last modification",
  "project.dashboards.table.search.placeholder": "Search",
  "project.dashboards.table.title.link.fault_event": (
    nblinks: number,
    translations: CustomTranslations,
  ) => `${capitalize(translations.EN.DASHBOARD)} used in ${nblinks} event${nblinks > 1 ? "s" : ""}`,
  "project.dashboards.table.title.link.summary": (translations: CustomTranslations) =>
    `${capitalize(translations.EN.DASHBOARD)} used in the home page`,
  ...schema_errors,
  ...runtime_errors,
  ...tooltip,
  "project.dashboard.actions.print.error": "An error occurred while printing.",
  "project.dashboard.actions.print.error_diagram_mismatch":
    "The number of diagram images to print does not match the number of diagrams in the dashboard.",
  "project.dashboard.actions.print.error_generating_image": "Failed to generate image for diagram.",
  "project.dashboard.actions.print.error_processing_diagrams":
    "Failed to process diagrams for printing.",
  "project.dashboard.actions.print.success": "PDF exported successfully.",
};

export default en;
